import React from "react";
import { useTranslation } from "react-i18next";
import type {
  TransformedCartTotals,
  TransformedCartFee,
  TransformedCartCoupon,
  TransformedCartShippingPackage,
  TransformedCartShippingRate,
} from "../../types/cartApiTypes";

interface CartTotalsProps {
  totals: TransformedCartTotals;
  fees: TransformedCartFee[];
  coupons: TransformedCartCoupon[]; // Still useful for knowing if any coupon is active
  needsShipping: boolean;
  hasCalculatedShipping: boolean;
  shippingRates: TransformedCartShippingPackage[];
  currencySymbol: string;
}

const CartTotals: React.FC<CartTotalsProps> = ({
  totals,
  fees,
  coupons,
  needsShipping,
  hasCalculatedShipping,
  shippingRates,
  currencySymbol,
}) => {
  const { t } = useTranslation();

  const selectedShippingRate: TransformedCartShippingRate | undefined =
    shippingRates
      .flatMap((pkg) => pkg.shippingRates)
      .find((rate) => rate.selected);

  const isFreeShipping = selectedShippingRate?.methodId === "free_shipping";
  const hasTax = totals.totalTax > 0;
  // Use the totalDiscount value from the API response to determine if the discount line should be shown
  const hasDiscount = totals.totalDiscount > 0;

  return (
    <div className="cart-totals">
      <h2>{t("cart.totalsTitle", "Cart Totals")}</h2>
      <div className="totals-item">
        <span>{t("cart.totals.subtotalWithTax", "Subtotal (incl. VAT)")}</span>
        <span>
          {totals.formattedTotalItems}
          {currencySymbol}
        </span>
      </div>

      {/* Display shipping */}
      {needsShipping && (
        <div className="totals-item">
          <span className={isFreeShipping ? "free-shipping-label" : ""}>
            {isFreeShipping
              ? t("cart.totals.freeShipping", "Free Shipping")
              : t("cart.totals.otherShipping", "Shipping")}
          </span>
          <span>
            {hasCalculatedShipping
              ? `${totals.formattedTotalShipping}${currencySymbol}`
              : t("cart.shippingCalculating", "Calculating...")}
          </span>
        </div>
      )}

      {/* Display fees */}
      {fees.map((fee) => (
        <div key={fee.id} className="totals-item fee-item">
          <span>{fee.name}</span>
          <span>
            {fee.formattedTotal}
            {currencySymbol}
          </span>
        </div>
      ))}

      {/* Display total discount line if a discount exists */}
      {hasDiscount && (
        <div className="totals-item discount-item">
          <span>{t("cart.discount", "Discount")}</span>
          <span>
            -{totals.formattedTotalDiscount}
            {currencySymbol}
          </span>
        </div>
      )}

      {/* Display Tax Row */}
      {hasTax && (
        <div className="totals-item tax-item">
          <span>{t("cart.totals.vat", "VAT (10%)")}</span>
          <span>
            {totals.formattedTotalTax}
            {currencySymbol}
          </span>
        </div>
      )}

      {/* Grand Total */}
      <div className="totals-item grand-total">
        <strong>{t("cart.totals.total", "Total")}</strong>
        <strong>
          {totals.formattedTotalPrice}
          {currencySymbol}
        </strong>
      </div>

      {/* Checkout Button */}
      <div className="checkout-button-container">
        <a href="/checkout" className="button checkout-button">
          {t("cart.proceedToCheckout", "Proceed to Checkout")}
        </a>
      </div>
    </div>
  );
};

export default CartTotals;
